import React, { useState, useRef, useEffect } from "react";
import Slider from "react-slick";
import {
  Box,
  MobileStepper,
  CircularProgress,
  Button,
  SxProps,
  Backdrop,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { Close } from "@mui/icons-material";
import styled from "styled-components";
import "./CarouselImageUploader.css";
import ImgRemove from "../../../Photos/removeimg.svg";

// Import your icons
import defaultPlaceholder from "../../../Photos/Newuiphotos/Icons/noEvntphoto.svg";
import cameraIcon from "../../../Photos/Newuiphotos/Icons/camera.svg";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import toast from "react-hot-toast";
import FullScreenImageModal from "../Carousel/FullScreenImageModal ";
import UploadIcon from "@mui/icons-material/Upload";
import MessageModel from "../messageModel/messageModel";
// Import your delete message image
import Deletemessagemodel1 from "../../../Photos/Newuiphotos/Icons/Delete.webp";
import { compressMediaFiles } from "../../../helpers/imageCompressor/imageCompressor";

/** ---------------------------------------------------------------------------
 *  Custom arrow components for react-slick
 * ---------------------------------------------------------------------------
 */
const CustomPrevArrow = (props: any) => {
  const { className, style, onClick } = props;
  return (
    <div
      className={`${className} custom-prev-arrow`}
      style={style}
      onClick={onClick}
    >
      <IoIosArrowBack color="white" size={15} />
    </div>
  );
};

const CustomNextArrow = (props: any) => {
  const { className, style, onClick } = props;
  return (
    <div
      className={`${className} custom-next-arrow`}
      style={style}
      onClick={onClick}
    >
      <IoIosArrowForward color="white" size={15} />
    </div>
  );
};

/** ---------------------------------------------------------------------------
 *  Styled components for optional Add/Delete Buttons
 * ---------------------------------------------------------------------------
 */
const ActionButton = styled.div`
  position: absolute;
  background: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  bottom: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

/** ---------------------------------------------------------------------------
 *  Props for the CarouselImageUploader
 * ---------------------------------------------------------------------------
 */
export interface CarouselImage {
  url: string;
  alt: string;
  _id?: string;
  createdByObjectURL?: boolean;
}

export interface CarouselImageUploaderProps {
  /** Array of { url, alt } objects */
  images: CarouselImage[];

  /** Whether to show the add button for uploading new images */
  showAddButton?: boolean;
  /** Whether to show the delete button for each slide */
  showDeleteButton?: boolean;

  /** Called when user wants to upload a new image */
  onUpload?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Called when user wants to delete the current image */
  onDelete?: (currentIndexOrId: number | string) => void;

  /** If you have a loading state after uploading, you can trigger a spinner overlay */
  isLoading?: boolean;

  /** Whether or not to show the bottom stepper/dots */
  hideController?: boolean;

  /** Placeholder image if no images exist */
  placeholderImg?: string;
  /** Size of the placeholder icon (width/height) */
  placeholderSize?: string;
  isCarousel?: boolean;
  imgStyle?: React.CSSProperties | SxProps;
  imgBgStyle?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
  defaultImgStyle?: React.CSSProperties;
  onCustomUploadHandler?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => void;
  roundededImageUploader?: boolean;
  deleteMessageImg?: string;
}

// Image Uploader Modal Component
interface ImageUploaderModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  onRemove: () => void;
  imageUrl?: string;
  isLoading?: boolean;
  isCarouselMedia?: boolean;
}

const ImageUploaderModal: React.FC<ImageUploaderModalProps> = ({
  open,
  onClose,
  onUpload,
  onRemove,
  imageUrl = "",
  isLoading = false,
  isCarouselMedia = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [uploadedPreview, setUploadedPreview] = useState<string | null>(null);

  // Check if we have an existing image (either from props or newly uploaded)
  const hasExistingImage = !!imageUrl;

  useEffect(() => {
    if (!open) {
      // Reset state when modal closes
      setUploadedImage(null);
      setUploadedPreview(null);
    }
  }, [open]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // Use your existing compression function
      const compressedFiles = await compressMediaFiles([file], {});
      if (compressedFiles.validFiles.length > 0) {
        setUploadedImage(compressedFiles.validFiles[0]);
        setUploadedPreview(URL.createObjectURL(compressedFiles.validFiles[0]));
      } else {
        toast.error("Failed to process image");
      }
    } catch (error) {
      toast.error("Error processing image");
      e.target.value = "";
    }
  };

  const handleUploadImage = () => {
    if (uploadedImage) {
      onUpload(uploadedImage);
    }
  };

  return (
    <Backdrop open={open} sx={{ zIndex: "9 !important" }} onClick={onClose}>
      <Card
        sx={{
          maxWidth: 400,
          width: "100%",
          position: "absolute",
          borderRadius: 4,
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "grey.500",
          }}
        >
          <Close />
        </IconButton>

        <CardContent sx={{ p: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            {(uploadedPreview || imageUrl) && (
              <Box
                sx={{
                  width: "100%",
                  height: 200,
                  position: "relative",
                  borderRadius: 6,
                  overflow: "hidden",
                  mb: 2,
                }}
              >
                <img
                  src={uploadedPreview || imageUrl}
                  alt="Image preview"
                  style={{
                    objectFit: "contain",
                    width: "100%",
                    height: "100%",
                  }}
                />
              </Box>
            )}

            {!uploadedPreview && !imageUrl ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                  id="image-upload"
                />
                <Button
                  component="label"
                  htmlFor="image-upload"
                  variant="contained"
                  startIcon={<UploadIcon />}
                  sx={{
                    display: "flex !important",
                    justifyContent: "center !important",
                    alignItems: "center !important",
                    color: "white !important",
                    bgcolor: "#00875A",
                    "&:hover": {
                      bgcolor: "#006C46",
                    },
                    borderRadius: 6,
                    textTransform: "none",
                    py: 1.5,
                    px: 3,
                    width: "90%",
                  }}
                >
                  {isCarouselMedia ? "Upload Cover Image" : "Upload Image"}
                </Button>
              </>
            ) : uploadedPreview ? (
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                sx={{
                  color: "white !important",
                  bgcolor: "#00875A",
                  "&:hover": {
                    bgcolor: "#006C46",
                  },
                  borderRadius: 6,
                  textTransform: "none",
                  py: 1.5,
                  px: 3,
                  width: "90%",
                }}
                onClick={handleUploadImage}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Upload Image"
                )}
              </Button>
            ) : (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                  id="image-upload"
                />
                <Button
                  component="label"
                  htmlFor="image-upload"
                  variant="contained"
                  startIcon={<UploadIcon />}
                  sx={{
                    display: "flex !important",
                    justifyContent: "center !important",
                    alignItems: "center !important",
                    color: "white !important",
                    bgcolor: "#00875A",
                    "&:hover": {
                      bgcolor: "#006C46",
                    },
                    borderRadius: 3,
                    textTransform: "none",
                    py: 1.5,
                    px: 3,
                    width: "90%",
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : isCarouselMedia ? (
                    "Update Cover Image"
                  ) : (
                    "Replace Image"
                  )}
                </Button>
              </>
            )}

            {imageUrl && (
              <Button
                onClick={onRemove}
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  borderStyle: "dashed",
                  textTransform: "none",
                  color: "#FF3838",
                  borderColor: "#FF3838",
                  "&:hover": {
                    borderColor: "grey.400",
                  },
                  py: 1.5,
                  px: 3,
                  width: "90%",
                }}
              >
                <img
                  style={{ paddingRight: "10px" }}
                  src={ImgRemove}
                  alt="remove image"
                />
                {isCarouselMedia ? "Remove Cover Image" : "Remove Image"}
              </Button>
            )}

            {/* {uploadedPreview && (
              <Button
                onClick={() => {
                  setUploadedImage(null);
                  setUploadedPreview(null);
                }}
                variant="outlined"
                sx={{
                  borderWidth: 2,
                  borderRadius: 6,
                  borderStyle: "dashed",
                  textTransform: "none",
                  color: "grey.500",
                  borderColor: "#9F9E9E",
                  "&:hover": {
                    borderColor: "#9F9E9E",
                  },
                  py: 1.5,
                  px: 3,
                  width: "90%",
                }}
              >
                {isCarouselMedia ? "Remove Image" : "Remove Selected Image"}
              </Button>
            )} */}
          </Box>
        </CardContent>
      </Card>
    </Backdrop>
  );
};

/** ---------------------------------------------------------------------------
 *  Main Reusable Component
 * ---------------------------------------------------------------------------
 */
const CarouselImageUploader = ({
  images,
  showAddButton = true,
  showDeleteButton = true,
  onUpload,
  onDelete,
  isLoading = false,
  hideController = true,
  placeholderImg = defaultPlaceholder,
  placeholderSize = "40px",
  isCarousel = false,
  imgStyle = {},
  imgBgStyle = {},
  containerStyle = {},
  defaultImgStyle = {},
  onCustomUploadHandler,
  roundededImageUploader = false,
  deleteMessageImg = Deletemessagemodel1,
}: CarouselImageUploaderProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const swiperRef = useRef<SwiperRef>(null);
  const sliderRef = useRef<Slider | null>(null);
  const [swiperIndex, setSwiperIndex] = useState(0);
  const prevImagesLengthRef = useRef<number>(images?.length ?? 0);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [isAtBeginning, setIsAtBeginning] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string>("");
  const [currentAlt, setCurrentAlt] = useState<string>("");
  const [fullscreenOpen, setFullScreenOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // States for delete confirmation
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);
  const [currentDeleteIndex, setCurrentDeleteIndex] = useState<number | null>(
    null
  );
  const [currentDeleteId, setCurrentDeleteId] = useState<string | null>(null);

  // Slick carousel settings
  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    // arrows: false,
    // swipeToSlide: false,
    slidesToShow: 1,
    slidesToScroll: 1,

    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    customPaging: (i: number) => <div></div>, // Hide default dots icon
    appendDots: (dots: React.ReactNode) => (
      <div>
        <ul style={{ margin: "0px" }}>{dots}</ul>
      </div>
    ),
    beforeChange: (current: number, next: number) => {
      setActiveStep(next);
      setIsDragging(true);
    },
    afterChange: (current: number) => {
      setIsDragging(false);
      setActiveStep(current);
      if (swiperRef?.current) {
        swiperRef.current.swiper.slideTo(current);
      }
    },
  };

  // const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   // Check if onUpload is defined and we actually have at least one file
  //   if (onUpload && e.target.files && e.target.files.length > 0) {
  //     onUpload(e);
  //   }
  // };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (onUpload) {
      onUpload(e);
    }
  };

  useEffect(() => {
    // Only run if NOT using the carousel and we have previously had images
    if (!isCarousel && prevImagesLengthRef.current > 0) {
      // Compare old length to the new length
      if (images?.length > prevImagesLengthRef.current) {
        // We just added images, so jump to the last index
        setActiveStep(images.length - 1);

        if (sliderRef.current) {
          sliderRef.current.slickGoTo(images.length - 1);
        }
        if (swiperRef.current) {
          swiperRef.current.swiper.slideTo(images.length - 1);
        }
      }
    }

    // Always update the ref after our checks
    prevImagesLengthRef.current = images?.length ?? 0;
  }, [images, isCarousel]);

  const handleUploadModalOpen = () => {
    setIsUploadModalOpen(true);
  };

  const handleImageUpload = (file: File) => {
    // Create a synthetic event with the file
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);

    const syntheticEvent = {
      target: {
        files: dataTransfer.files,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    if (onUpload) {
      onUpload(syntheticEvent);
    }

    setIsUploadModalOpen(false);
  };

  const handleRemoveImage = () => {
    // Close the upload modal first
    setIsUploadModalOpen(false);

    // If we're in carousel mode and have a current image
    if (images && images.length > 0 && activeStep >= 0) {
      // Get the current image ID or index
      const currentImage = images[activeStep];
      if (currentImage && currentImage._id) {
        // If we have an ID, use it
        setCurrentDeleteId(currentImage._id);
      } else {
        // Otherwise use the index
        setCurrentDeleteIndex(activeStep);
      }
    }

    // Open delete confirmation dialog
    setIsDeleteDialogOpen(true);
  };

  // Function to handle deletion confirmation
  const handleConfirmDelete = () => {
    setIsDeleteInProgress(true);

    try {
      if (currentDeleteId && onDelete) {
        // Delete by ID
        onDelete(currentDeleteId);
      } else if (currentDeleteIndex !== null && onDelete) {
        // Delete by index
        onDelete(currentDeleteIndex);
      }
    } catch (error) {
      console.error("Error during image deletion:", error);
      toast.error("An error occurred while deleting the image");
    }

    // Reset state
    setIsDeleteInProgress(false);
    setIsDeleteDialogOpen(false);
    setCurrentDeleteId(null);
    setCurrentDeleteIndex(null);
  };

  // Function to handle delete cancellation
  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setCurrentDeleteId(null);
    setCurrentDeleteIndex(null);
  };

  const renderBoxWithBackground = (imageUrl?: string, imageAlt?: string) => {
    return (
      <Box
        // key={index}
        className="carousel-slide"
        sx={{
          width: "100%",
          height: "auto",
          maxHeight: roundededImageUploader ? 130 : 280,
          minHeight: roundededImageUploader ? 130 : 180,
          display: "flex !important",
          alignItems: "center",
          justifyContent: roundededImageUploader ? "center" : "",
          position: "relative",
          "::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "99%",
            height: "100%",
            background: `url(${imageUrl}) no-repeat center center`,
            backgroundSize: "cover",
            filter: "blur(4px)",
            zIndex: -1,
            borderRadius: "16px",
            ...imgBgStyle,
          },
          outline: "none",
          "&:focus-visible": {
            outline: "none",
          },
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          //   alt={imageAlt}
          alt={imageAlt}
          draggable={false}
          sx={{
            display: "block",
            width: "100%",
            height: "auto",
            maxHeight: 180,
            minHeight: 180,
            objectFit: "contain",
            borderRadius: "10px",
            color: "black",
            ...imgStyle,
            outline: "none",
            "&:focus-visible": {
              outline: "none",
            },
          }}
          onClick={(e) => {
            if (!isCarousel || isDragging) return;
            setCurrentAlt(imageAlt ?? "");
            setCurrentSrc(imageUrl ?? "");
            setFullScreenOpen(true);
          }}
          onError={(e) => {
            // toast.dismiss();
            // toast.error("Failed to render image");
            // (e.currentTarget as HTMLImageElement).src = placeholderImg;
          }}
        />
      </Box>
    );
  };

  return (
    <div className="carousel-uploader-wrapper ">
      <div
        className="carousel-uploader__img-container"
        style={{ ...containerStyle }}
      >
        {images?.length > 0 ? (
          <div
            style={{
              position: "relative",
              width: roundededImageUploader ? "130px" : "100%",
            }}
          >
            {/* {!isCarousel ? (
              renderBoxWithBackground(
                images[activeStep].url,
                images[activeStep].alt
              )
            ) : ( */}
            <Slider
              {...settings}
              ref={sliderRef}
              className={roundededImageUploader ? "custom-slider" : ""}
            >
              {images.map(
                (image, index) => renderBoxWithBackground(image.url, image.alt)
                // <Box
                //   key={index}
                //   className="carousel-slide"
                //   sx={{
                //     position: "relative",
                //     "::before": {
                //       content: '""',
                //       position: "absolute",
                //       top: 0,
                //       left: 0,
                //       width: "100%",
                //       height: "100%",
                //       background: `url(${image.url}) no-repeat center center`,
                //       backgroundSize: "cover",
                //       filter: "blur(4px)",
                //       zIndex: -1,
                //     },
                //   }}
                // >
                //   <Box
                //     component="img"
                //     src={image.url}
                //     alt={image.alt || `Photo ${index}`}
                //     draggable={false}
                //     sx={{
                //       display: "block",
                //       width: "100%",
                //       height: "auto",
                //       maxHeight: 180,
                //       minHeight: 180,
                //       objectFit: "contain",
                //       borderRadius: "10px",
                //     }}
                //     onError={(e) => {
                //       (e.currentTarget as HTMLImageElement).src =
                //         placeholderImg;
                //     }}
                //   />
                // </Box>
              )}
            </Slider>
            {/* )} */}
          </div>
        ) : (
          // No images - placeholder display
          <Box className="carousel-placeholder-box">
            <Box
              component="img"
              src={placeholderImg}
              alt="No images"
              draggable={false}
              sx={{
                maxHeight: 180,
                minHeight: 180,
                width: placeholderSize,
                height: 180,
                // margin: "20px 0",
                objectFit: "contain",
                ...defaultImgStyle,
              }}
            />

            {isLoading && (
              <CircularProgress
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            )}
          </Box>
        )}

        {/* Camera icon for adding/updating images */}
        {showAddButton && (onUpload || onCustomUploadHandler) && (
          <>
            <ActionButton
              style={{
                right: "10px",
                bottom: "16px",
                left: roundededImageUploader ? "62%" : "",
              }}
              onClick={(e) => {
                if (onCustomUploadHandler) {
                  onCustomUploadHandler(e);
                } else {
                  handleUploadModalOpen();
                }
              }}
            >
              <img
                src={cameraIcon}
                alt="Add/Edit"
                style={{ width: 20, height: 20 }}
              />
            </ActionButton>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileInputChange}
            />
          </>
        )}
      </div>

      {!hideController && images.length > 1 && (
        <MobileStepper
          steps={images?.length || 0}
          position="static"
          activeStep={activeStep}
          nextButton={null}
          backButton={null}
          sx={{
            justifyContent: "center",
            position: "absolute",
            bottom: "-24px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "none",
            zIndex: "200",
            marginTop: "24px",
          }}
        />
      )}

      {/* Delete confirmation dialog */}
      {isDeleteDialogOpen && (
        <MessageModel
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          messageType={isCarousel ? "Delete Cover Image" : "Delete Image"}
          message={
            isCarousel
              ? "Are you sure you want to delete this cover image?"
              : "Are you sure you want to delete this image?"
          }
          isLoading={isDeleteInProgress}
          img={deleteMessageImg}
        />
      )}

      {/* Fullscreen image modal */}
      <FullScreenImageModal
        isOpen={fullscreenOpen}
        setIsOpen={setFullScreenOpen}
        imgSrc={currentSrc}
        imgAlt={currentAlt}
      />

      {/* Image uploader modal */}
      <ImageUploaderModal
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleImageUpload}
        onRemove={handleRemoveImage}
        imageUrl={images && images.length > 0 ? images[activeStep]?.url : ""}
        isLoading={isLoading}
        isCarouselMedia={isCarousel}
      />
    </div>
  );
};

export default CarouselImageUploader;
